import { useEffect, useState } from 'react';
import { ChevronRightIcon, UserIcon } from 'lucide-react';
import type { BridgeAccount } from '@/lib/bridge';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { cn } from '@/lib/utils';

/**
 * 账号 list item：头像 + 昵称 + 状态徽标 + 副标题 + chevron。
 *
 * 对齐搬小书 `apps/web/src/app/mobile/tools/account-card.tsx` —— 账号选择用卡片行，
 * 点了交给原生 `account.pick` 弹选择器，不用下拉框。
 */
type Props = {
  account: BridgeAccount | null;
  label?: string;
  detail?: string;
  emptyText?: string;
  onClick?: () => void;
  disabled?: boolean;
  /** 嵌在别的卡片里（如 StepCard）时用：浅灰底、无边框，和 SelectRow 一致 */
  flat?: boolean;
  /** 副标题里是否带 `ID xxx`（默认带；id 是内部拼接值的 App 可关掉，改在 label 里放平台号） */
  showId?: boolean;
};

function Avatar({ account }: { account: BridgeAccount | null }) {
  const [broken, setBroken] = useState(false);
  useEffect(() => setBroken(false), [account?.avatar]);

  if (!account?.avatar || broken) {
    return (
      <span
        className="bg-muted text-muted-foreground flex size-11 shrink-0 items-center justify-center rounded-full"
        aria-hidden
      >
        <UserIcon className="size-5" />
      </span>
    );
  }
  return (
    <img
      className="bg-muted size-11 shrink-0 rounded-full object-cover"
      src={account.avatar}
      alt=""
      referrerPolicy="no-referrer"
      onError={() => setBroken(true)}
    />
  );
}

export function AccountCard({
  account,
  label,
  detail,
  emptyText = '选择账号',
  onClick,
  disabled,
  flat,
  showId = true,
}: Props) {
  const subtitle = [label, account && showId ? `ID ${account.id}` : null, detail]
    .filter(Boolean)
    .join(' · ');

  const body = (
    <>
      <Avatar account={account} />
      <div className="min-w-0 flex-1">
        <div className="flex min-w-0 items-center gap-2 text-callout font-medium ios:text-body ios:font-semibold">
          <span className="truncate">{account ? account.nickname : emptyText}</span>
          {account ? (
            <Badge variant={account.canWrite ? 'secondary' : 'outline'} className="shrink-0">
              {account.canWrite ? '可写' : '只读'}
            </Badge>
          ) : null}
        </div>
        <p className="text-muted-foreground mt-0.5 truncate text-footnote">{subtitle}</p>
      </div>
      {onClick ? <ChevronRightIcon className="text-muted-foreground size-4 shrink-0" /> : null}
    </>
  );

  return (
    <Card className={cn('gap-0 rounded-card p-0', flat && 'bg-muted/60 ring-0 ios:bg-transparent')}>
      {onClick ? (
        <button
          type="button"
          onClick={onClick}
          disabled={disabled}
          className={cn(
            'active:bg-accent flex w-full items-center gap-3 px-4 py-3.5 text-left transition-colors',
            flat && 'ios:px-0 ios:active:bg-transparent ios:active:opacity-60',
            disabled && 'opacity-60',
          )}
        >
          {body}
        </button>
      ) : (
        <div className={cn('flex items-center gap-3 px-4 py-3.5', flat && 'ios:px-0')}>{body}</div>
      )}
    </Card>
  );
}
